import type { IconLike } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import {
    advancedSearchPathFragment,
    bundleManagerPathFragment,
    canvasPathFragment,
    deletedPathLegacy,
    executionsPathFragment,
    logSubPageKey,
    settingsPathFragment,
    storageAnalysisSubPageKey,
    trashPath,
    usersRolesPathFragment,
    workspaceSyncsSubPageKey,
} from "utils/RouterManager/constants";
import { getActiveSubPageFlag } from "utils/RouterManager/subPageLinks";

// Screen (non-record) → favicon icon.
//
// These are the screens that are NOT a single record view (execution live
// monitor, advanced search, user & role management, workspace settings,
// container logs, storage analysis, object canvas, bundle manager, trash).
// Before this map they fell through to `resetFavicon()` and showed the plain
// app default in the browser tab. Each icon here is deliberately the SAME glyph
// the screen already uses in the left-nav three-dots menu (createMenuParent.tsx)
// and/or its own PageHeader `titleIcon`, so the tab icon reads as the screen the
// user is looking at — we reuse the existing nav/route icon source rather than
// inventing a second one.
const SUB_PAGE_FLAG_ICON: Record<string, IconLike> = {
    // ?executions — Executions / execution live monitor (nav: flashOutline)
    [executionsPathFragment]: IconCollection.flashOutline,
    // ?settings — Workspace Settings (nav + header: settingsIcon)
    [settingsPathFragment]: IconCollection.settingsIcon,
    // ?storage-analysis — Storage analysis (nav + header: database)
    [storageAnalysisSubPageKey]: IconCollection.database,
    // ?workspace-syncs — Workspace syncs (nav + header: sync)
    [workspaceSyncsSubPageKey]: IconCollection.sync,
    // ?bundle-manager — Bundle Manager (header: cartArrowDown)
    [bundleManagerPathFragment]: IconCollection.cartArrowDown,
    // ?logs — Workspace/Project/Bundle Logs (nav: alignRight)
    [logSubPageKey]: IconCollection.alignRight,
    // ?advanced-search — Advanced Search (nav: magnify)
    [advancedSearchPathFragment]: IconCollection.magnify,
    // ?users-roles — User & Role Management (nav + header: shieldAccountOutline)
    [usersRolesPathFragment]: IconCollection.shieldAccountOutline,
    // ?canvas — Object Canvas (nav: vectorPolyLine)
    [canvasPathFragment]: IconCollection.vectorPolyLine,
};

// Matches the canvas screen when it is expressed as a PATH segment
// (/:recordTypeName/:recordId/canvas[/template|/object]) rather than the
// ?canvas query flag.
const CANVAS_PATH_RE = new RegExp(`/${canvasPathFragment}(/|$)`);

/**
 * Resolve the favicon icon for a known non-record SCREEN, or undefined when the
 * current location is not one of them (a plain record view, or the workspace
 * home). Takes precedence over the record-icon logic so that, e.g.,
 * `/project?name=X&logs` shows the logs glyph rather than the project icon.
 *
 * @param pathname current `location.pathname`
 * @param search   current `location.search` (may include the leading "?")
 */
export const resolveScreenFaviconIcon = (
    pathname: string,
    search: string
): IconLike | undefined => {
    // 1) Top-level, path-based screens (not workspace sub-page query flags).
    if (pathname === trashPath || pathname === deletedPathLegacy) {
        return IconCollection.deleteOutline;
    }
    if (CANVAS_PATH_RE.test(pathname)) {
        return IconCollection.vectorPolyLine;
    }
    // 2) Workspace / container sub-page query flags (?executions, ?settings, …,
    //    ?canvas). getActiveSubPageFlag tolerates a leading "?".
    const flag = getActiveSubPageFlag(search);
    if (flag && flag in SUB_PAGE_FLAG_ICON) {
        return SUB_PAGE_FLAG_ICON[flag];
    }
    return undefined;
};
